// noinspection t

import {computed, effect, inject} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {AutomaticRecordingSession, Scenario, TrainingRoom, UpsertAutomaticRecordingSessionMetaDataRequest} from "@models";
import {patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState} from "@ngrx/signals";
import {SimConfigurationService, SimEventService, SimSessionService} from "@services";
import dayjs from "dayjs";
import {debounceTime, distinctUntilChanged, filter, startWith} from "rxjs";
import {SimTimeService} from "src/app/core/services/sim-services/sim-time.service";
import {AutosaveConfig} from "src/app/shared/shared-components/autosave-widget/autosave-widget.component";
import {chunk} from "lodash-es";

type AutomaticRecordingSessionDialogStoreState = {
	isLoading: boolean;
	eventId: string | null;
	session: AutomaticRecordingSession | null;
	activeStationIndex: number;
	autosaveConfig: AutosaveConfig;
	isAutosaving: boolean;
};

type AutomaticRecordingSessionDialogStoreProps = {
	metadataForm: FormGroup<{
		title: FormControl<string | null>;
		date: FormControl<Date | null>;
		startTime: FormControl<string | null>;
	}>;
	stationForms: FormArray<AutomaticRecordingStation_Form>;
	sessionService: SimSessionService;
	eventService: SimEventService;
	scheduleForm: FormArray<AutomaticRecordingScheduleEntry_Form>;
};

type AutomaticRecordingStation_Form = FormGroup<{
	trainingRoom: FormControl<TrainingRoom | null>;
	scenario: FormControl<Scenario | null>;
}>;

type AutomaticRecordingScheduleEntry_Form = FormGroup<{
	runDurationInMinutes: FormControl<number>;
	intermissionDurationInMinutes: FormControl<number>;
}>;

export const AutomaticRecordingSessionDialogStore = signalStore(
	withState<AutomaticRecordingSessionDialogStoreState>(() => ({
		isLoading: true,
		eventId: null,
		session: null,
		activeStationIndex: -1,
		autosaveConfig: {},
		isAutosaving: false,
	})),
	withProps(
		() =>
			({
				metadataForm: new FormGroup({
					title: new FormControl<string | null>(null, [Validators.required]),
					date: new FormControl<Date | null>(null, [Validators.required]),
					startTime: new FormControl<string | null>(null, [Validators.required]),
				}),
				stationForms: new FormArray<AutomaticRecordingStation_Form>([]),
				scheduleForm: new FormArray<AutomaticRecordingScheduleEntry_Form>([]),
				sessionService: inject(SimSessionService),
				eventService: inject(SimEventService),
			}) satisfies AutomaticRecordingSessionDialogStoreProps,
	),
	withComputed((store) => ({
		activeStationForm: computed(() => {
			if (store.activeStationIndex() < 0 || store.activeStationIndex() >= store.stationForms.length) {
				return undefined;
			}
			return store.stationForms.at(store.activeStationIndex());
		}),
		stations: toSignal<AutomaticRecordingStation_Form["value"][]>(
			store.stationForms.valueChanges.pipe(startWith(store.stationForms.value)),
			{requireSync: true},
		),
		schedule: toSignal<AutomaticRecordingScheduleEntry_Form["value"][]>(
			store.scheduleForm.valueChanges.pipe(startWith(store.scheduleForm.value)),
			{requireSync: true},
		),
	})),
	// forms helper
	withMethods((store) => ({
		_populateForms(session: AutomaticRecordingSession) {
			const date = new Date(session.startTime);
			const startTime = dayjs(new Date(session.startTime)).format("h:mm A");
			store.metadataForm.setValue(
				{
					title: session.title,
					date,
					startTime,
				},
				{emitEvent: false},
			);

			store.stationForms.clear({emitEvent: false});
			session.stations.forEach((station) => {
				store.stationForms.push(
					new FormGroup({
						trainingRoom: new FormControl<TrainingRoom | null>(station.room ?? null, [Validators.required]),
						scenario: new FormControl<Scenario | null>(station.scenario ?? null, [Validators.required]),
					}),
					{emitEvent: false},
				);
			});

			store.scheduleForm.clear({emitEvent: false});
			chunk(session.schedule, 2).forEach(([run, intermission]) => {
				const runDuration = SimTimeService.fromMillisecondsToMinutes(run.duration);
				const intermissionDuration = intermission
					? SimTimeService.fromMillisecondsToMinutes(intermission.duration)
					: SimConfigurationService.settings.automaticRecordingSessionDefaultIntermissionDuration;

				store.scheduleForm.push(createScheduleEntryForm(runDuration, intermissionDuration), {
					emitEvent: false,
				});
			});

			// emit events to update the computed signals
			store.stationForms.patchValue([]);
			store.scheduleForm.patchValue([]);

			// Somehow the template does not update with the form state changes after this function is called
			// For example when the metadataForm is updated
			// This is a workaround to reset the active station index, and the ui associated with it
			const lastActiveIndex = store.activeStationIndex();
			patchState(store, {activeStationIndex: -1});
			setTimeout(() =>
				patchState(store, {
					activeStationIndex: lastActiveIndex !== -1 ? lastActiveIndex : session.stations.length - 1,
				}),
			);
		},
	})),
	// helpers
	withMethods((store) => ({
		async _upsertMetadata(value: typeof store.metadataForm.value) {
			const sessionId = store.session()?.id;
			const request = {
				sessionId,
				eventId: store.eventId()!,
				title: value.title!,
				startTime: SimTimeService.addTimeStringToDate(value.date!, value.startTime!),
			} satisfies UpsertAutomaticRecordingSessionMetaDataRequest;
			const promise = store.sessionService.upsertAutomaticRecordingSessionMetaData(request);
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});

			store._populateForms(store.session()!);

			// Assign session to event (if freshly created)
			if (!sessionId) {
				await store.eventService.addSessionToEvent(store.eventId()!, store.session()!.id);
			}
		},
		async _upsertStation() {
			const activeStation = store.activeStationForm()?.value;
			if (!activeStation) {
				return;
			}
			const sessionId = store.session()?.id;
			const roomId = activeStation.trainingRoom?.id;
			const scenarioId = activeStation.scenario?.id;
			if (!sessionId || !roomId || !scenarioId) {
				return;
			}

			const oldStation = store.session()?.stations.at(store.activeStationIndex());
			if (oldStation && oldStation.roomId === roomId && oldStation.scenarioId === scenarioId) {
				// No changes, no need to update
				return;
			}

			const stationId = store.session()?.stations.at(store.activeStationIndex())?.id;
			const promise = stationId
				? store.sessionService.updateStationInAutomaticRecordingSession({
						stationId,
						sessionId,
						roomId,
						scenarioId,
					})
				: store.sessionService.addStationToAutomaticRecordingSession({
						sessionId,
						roomId,
						scenarioId,
					});
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});
		},
	})),
	withMethods((store) => ({
		async _upsertScheduleEntry(durationInMinutes: number, entryId?: string) {
			const sessionId = store.session()?.id;
			if (!sessionId) {
				throw new Error("Session ID is required to upsert schedule entry.");
			}

			const duration = SimTimeService.fromMinutesToMilliseconds(durationInMinutes);

			const promise = entryId
				? store.sessionService.updateDurationOfScheduleEntryInAutomaticRecordingSession({
						sessionId,
						scheduleEntryId: entryId,
						duration,
					})
				: store.sessionService.addRunScheduleEntryToAutomaticRecordingSession({
						sessionId,
						duration,
					});
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});
		},
		_observeFormChanges() {
			store.metadataForm.valueChanges
				.pipe(
					filter(() => store.metadataForm.valid),
					debounceTime(500),
					distinctUntilChanged(
						(a, b) =>
							a.title === b.title &&
							a.date?.toLocaleDateString() === b.date?.toLocaleDateString() &&
							a.startTime === b.startTime,
					),
				)
				.subscribe(store._upsertMetadata);

			store.stationForms.valueChanges
				.pipe(
					filter(() => store.stationForms.valid && !!store.activeStationForm()?.valid),
					debounceTime(500),
				)
				.subscribe(store._upsertStation);
		},
	})),
	withMethods((store) => ({
		async initializeStore(eventDay: string | Date, eventId: string, sessionId?: string) {
			patchState(store, {eventId});
			const date = new Date(eventDay);
			if (date) {
				store.metadataForm.controls.date.setValue(date);
			}
			if (!sessionId) {
				patchState(store, {isLoading: false});
				return;
			}

			const session = (await store.sessionService.getSessionById(sessionId)) as AutomaticRecordingSession;
			store._populateForms(session);
			patchState(store, {session, isLoading: false});
		},
		stationAdded() {
			const stationForm: AutomaticRecordingStation_Form = new FormGroup({
				trainingRoom: new FormControl<TrainingRoom | null>(null, Validators.required),
				scenario: new FormControl<Scenario | null>(null, Validators.required),
			});
			store.stationForms.push(stationForm);
			patchState(store, {activeStationIndex: store.stationForms.length - 1});
		},
		stationSelected(index: number) {
			patchState(store, {activeStationIndex: index});
		},
		async stationDeleted(index: number) {
			store.stationForms.removeAt(index);
			if (index === store.activeStationIndex()) {
				patchState(store, {activeStationIndex: index - 1});
			}
			if (store.activeStationIndex() >= store.stationForms.length) {
				patchState(store, {activeStationIndex: store.stationForms.length - 1});
			}
			const session = store.session();
			const station = session?.stations.at(index);
			if (!station?.id || !session?.id) {
				return;
			}
			const promise = store.sessionService.removeStationFromAutomaticRecordingSession({
				stationId: station.id,
				sessionId: session.id,
			});
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});
		},
		scheduleEntryAdded() {
			const form = createScheduleEntryForm(
				SimConfigurationService.settings.automaticRecordingSessionDefaultRunDuration,
				SimConfigurationService.settings.automaticRecordingSessionDefaultIntermissionDuration,
			);
			store.scheduleForm.push(form);
			store._upsertScheduleEntry(form.getRawValue().runDurationInMinutes);
		},
		scheduleEntryUpdated(indexInForm: number, type: "run" | "intermission", duration: number) {
			// Each entry has a run and an intermission, so we multiply by 2
			const index = indexInForm * 2 + (type === "intermission" ? 1 : 0);
			const entryId = store.session()?.schedule.at(index)?.id;
			store._upsertScheduleEntry(duration, entryId);
		},
		async scheduleEntryDeleted(indexInForm: number) {
			const session = store.session();

			if (!session) {
				throw new Error("Session ID is required to delete schedule entry.");
			}

			const index = indexInForm * 2; // Each entry has a run and an intermission, so we multiply by 2
			const entryId = session.schedule.at(index)?.id;

			store.scheduleForm.removeAt(indexInForm);
			const promise = store.sessionService.removeRunScheduleEntryFromAutomaticRecordingSession({
				sessionId: session.id,
				scheduleEntryId: entryId!,
			});
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});
		},
	})),
	withHooks((store) => ({
		onInit() {
			store._observeFormChanges();

			effect((onCleanup) => {
				const promise = store.autosaveConfig().promise;
				if (!promise) {
					return;
				}

				patchState(store, {isAutosaving: true});

				let timeout: number;
				promise.finally(() => {
					timeout = setTimeout(() => patchState(store, {isAutosaving: false}), 175);
				});

				onCleanup(() => clearTimeout(timeout));
			});
		},
	})),
);

function createScheduleEntryForm(
	runDurationInMinutes: number,
	intermissionDurationInMinutes: number,
): AutomaticRecordingScheduleEntry_Form {
	return new FormGroup({
		runDurationInMinutes: new FormControl(runDurationInMinutes, {
			nonNullable: true,
			validators: [Validators.required],
		}),
		intermissionDurationInMinutes: new FormControl(intermissionDurationInMinutes, {
			nonNullable: true,
			validators: [Validators.required],
		}),
	});
}
